import React from "react";
import { Container, Header, Segment, List, Icon } from "semantic-ui-react";
import "../../node_modules/semantic-ui-css/semantic.min.css";
import Typography from "@mui/material/Typography";

const Help = () => {
  return (
    <Container
      style={{ marginTop: "0.5em", padding: "1em", marginBottom: "56px" }}
    >
      <Typography
        variant="h5"
        gutterBottom
        align="center"
        sx={{
          color: "primary.dark",
          fontWeight: 600,
          mb: 2,
          mt: 0.5,
          fontSize: { xs: "1.5rem", md: "1.75rem" },
          letterSpacing: "-0.02em",
        }}
      >
        <Icon name="info circle" />
        Getting Started
      </Typography>

      <Segment raised style={{ marginTop: "1.5em" }}>
        <Header as="h3" color="teal">
          <Icon name="sign in alternate" /> Access
        </Header>
        <List bulleted>
          <List.Item>
            Currently, this site supports only Google Sign-In.
          </List.Item>
        </List>
      </Segment>

      <Segment raised style={{ marginTop: "1.5em" }}>
        <Header as="h3" color="teal">
          <Icon name="lightbulb" /> Requesting a Feature
        </Header>
        <List bulleted>
          <List.Item>
            To submit a feature request, please navigate to{" "}
            <b>Help -> Submit Feature</b> from the main menu.
          </List.Item>
        </List>
      </Segment>

      <Segment raised style={{ marginTop: "1.5em" }}>
        <Header as="h3" color="teal">
          <Icon name="search" /> Researching Companies
        </Header>
        <List bulleted>
          <List.Item>
            To research a company, enter its name or ticker symbol in the{" "}
            <b>Search Input</b> field and select the desired company from the
            search results.
          </List.Item>
          <List.Item>
            If a company's name consists of multiple words (e.g., Service Now),
            try searching for individual words such as "Service" or "Now".
          </List.Item>
          <List.Item>
            Once a company is selected, its detailed information will be
            displayed on the main page.
          </List.Item>
        </List>
      </Segment>

      <Segment raised style={{ marginTop: "1.5em" }}>
        <Header as="h3" color="teal">
          <Icon name="building" /> Company Details Screen
        </Header>
        <List bulleted>
          <List.Item>
            The <b>Company Details</b> tab presents essential information about
            the selected company.
          </List.Item>
          <List.Item>
            The <b>Intrinsic Value (IV)</b> tab provides details on four
            different IV calculation methods.
          </List.Item>
          <List.Item>
            The <b>Financials</b> tab presents the company's financial
            statements (Income Statement, Balance Sheet, and Cash Flow
            Statement) for the past five years.
          </List.Item>
          <List.Item>
            The <b>Fundamental Analysis</b> tab offers color-coded results of
            the fundamental analysis:
            <List.List style={{ marginLeft: "1em" }}>
              <List.Item>
                Sales/Revenue Analysis: historical sales data, growth rates,
                trends, and key observations.
              </List.Item>
              <List.Item>
                Earnings Analysis: historical earnings growth, rates, trends,
                and analyst forecasts.
              </List.Item>
              <List.Item>
                Cash Flow Analysis: historical cash flow growth, rates, and
                trends.
              </List.Item>
              <List.Item>
                Economic Moat Analysis: historical growth of Gross Margin and
                Profit Margin.
              </List.Item>
              <List.Item>
                Debt Analysis: current ratio, debt-to-equity ratio, and debt
                servicing ratio.
              </List.Item>
              <List.Item>
                Fair Price Analysis: valuation metrics such as PEG ratio,
                Intrinsic Value, and Book Value.
              </List.Item>
            </List.List>
          </List.Item>
          <List.Item>
            <b>Technical Analysis</b> (Coming Soon!)
          </List.Item>
          <List.Item>
            The <b>Complete Report</b> tab consolidates all of the above
            information onto a single page.
          </List.Item>
          <List.Item>
            The <b>Compare to Peers</b> tab allows you to compare the selected
            company with its major industry peers.
          </List.Item>
        </List>
      </Segment>

      <Segment raised style={{ marginTop: "1.5em" }}>
        <Header as="h3" color="teal">
          <Icon name="columns" /> Compare Stocks
        </Header>
        <List bulleted>
          <List.Item>
            The <b>Compare Stocks</b> feature enables you to select and compare
            up to four stocks.
          </List.Item>
          <List.Item>
            To remove a ticker from the selection, click the <b>x</b> icon next
            to it in the selected list.
          </List.Item>
        </List>
      </Segment>

      <Segment raised style={{ marginTop: "1.5em" }}>
        <Header as="h3" color="teal">
          <Icon name="filter" /> Screener
        </Header>
        <List bulleted>
          <List.Item>
            The <b>Screener</b> provides a comprehensive report of all stocks,
            including the underlying scoring methodology.
          </List.Item>
          <List.Item>
            Clicking on the column headers allows you to sort the data in
            ascending or descending order.
          </List.Item>
          <List.Item>
            To filter results, click the column filter icon (adjacent to the
            column label) and enter your search criteria.
          </List.Item>
          <List.Item>
            To clear the search criteria, return to the column, click the filter
            icon, and remove the entered criteria.
          </List.Item>
        </List>
      </Segment>

      <Segment raised style={{ marginTop: "1.5em" }}>
        <Header as="h3" color="teal">
          <Icon name="idea" /> Investing Ideas
        </Header>
        <List bulleted>
          <List.Item>
            This section offers curated investment ideas for your consideration.
          </List.Item>
          <List.Item>
            <b>Best of All</b>: Highlights top-performing companies that meet
            all fundamental criteria.
          </List.Item>
          <List.Item>
            <b>Top 50</b>: Presents a ranked list of the 50 highest-rated
            companies based on their fundamental scores.
          </List.Item>
          <List.Item>
            <b>Large Cap Growth</b>: Features large-capitalization companies
            that have passed fundamental screening.
          </List.Item>
          <List.Item>
            <b>Great Small & Mid companies</b>: Showcases promising small-cap
            and mid-cap companies that meet fundamental criteria.
          </List.Item>
          <List.Item>
            <b>Gems under $20</b>: Identifies companies of all sizes, priced
            below $20, that have passed fundamental screening.
          </List.Item>
          <List.Item>
            <b>Good Dividend Stocks</b>: Focuses on companies with attractive
            dividend yields and solid fundamentals.
          </List.Item>
          <List.Item>
            <b>Speculative Growth Stocks</b>: Includes companies with consistent
            revenue growth but current unprofitability.
          </List.Item>
        </List>
      </Segment>

      <Segment raised style={{ marginTop: "1.5em" }}>
        <Header as="h3" color="teal">
          <Icon name="users" /> Guru Portfolios
        </Header>
        <List bulleted>
          <List.Item>
            <b>Guru Portfolios</b>: provides insight into the stock holdings of
            prominent investment managers, offering a glimpse into their
            investment strategies
          </List.Item>
          <List.Item>
            Each menu item provides insights into companies held by prominent
            hedge fund managers, such as Bill Ackman and Guy Spier.
          </List.Item>
        </List>
      </Segment>
      <Segment raised style={{ marginTop: "1.5em" }}>
        <Header as="h3" color="teal">
          <Icon name="users" /> Curated Lists
        </Header>
        <List bulleted>
          <List.Item>
            <b>Curated Lists</b>: offers collections of stocks grouped by
            specific investment themes or strategies by SimplyStock
          </List.Item>
        </List>
      </Segment>
    </Container>
  );
};

export default Help;
